import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { observeBuildHash } from './lib/build-version.ts';
import { clog } from './lib/clog.ts';

/**
 * API client + TanStack Query hooks. Components import hooks from here
 * and consume the result; nothing in the page tree should call `fetch`
 * directly or hold server state in `useState` + `useEffect`.
 *
 * Conventions:
 *   - Query keys are arrays starting with the resource name.
 *   - Mutations invalidate the exact keys they affect.
 *   - All requests go through `fetchJson` so 401 / 4xx / 5xx surface
 *     uniformly to the query layer.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.technicalaccounting.net';

// Re-exported here so component code only imports from one module.
export const SIGN_IN_URL = `${API_URL}/v1/auth/sign-in`;
export const SIGN_UP_URL = `${API_URL}/v1/auth/sign-up`;
export const SIGN_OUT_URL = `${API_URL}/v1/auth/sign-out`;

export type OrgRole = 'admin' | 'member';

export interface OrgSummary {
  id: number;
  name: string;
  slug: string;
  workos_org_id: string | null;
  // User-uploaded square icon as a data URL, or null (fall back to a
  // linked repo's avatar, then a colored initial).
  icon: string | null;
  role: OrgRole;
}

export interface CurrentUser {
  id: number;
  email: string;
  name: string | null;
}

export interface MeResponse {
  user: CurrentUser;
  // Org the user picked at the WorkOS sign-in step; the SPA seeds the active
  // org from it on first load. null when the session carries no org context.
  selected_org_slug: string | null;
  orgs: OrgSummary[];
}

export interface Installation {
  id: number;
  installation_id: number;
  github_account_login: string;
  github_account_type: 'Organization' | 'User';
  suspended_at: string | null;
  uninstalled_at: string | null;
}

export interface Repo {
  id: number;
  installation_id: number;
  repo_full_name: string;
  default_branch: string | null;
}

/**
 * New-schema repository (`repositories` table, string-id `repo_<hash>`).
 * Powers the Repositories settings page and the backfill flow. A repo
 * is the canonical thing here — `github_installation_id` is just one
 * sync source among several (others: local skill, MCP, manual).
 */
export type RepositoryIngestMode = 'local_skill' | 'cloud_artifacts' | 'rest_only' | 'manual';
export type SourceProvider = 'github' | 'gitlab' | 'azure_devops';
export interface Repository {
  id: string;
  org_id: number;
  prefix: string;
  owner: string;
  name: string;
  default_branch: string;
  current_head_sha: string | null;
  /** Owner (org/user) avatar URL, used for the nav org switcher icon. */
  avatar_url: string | null;
  provider: SourceProvider;
  github_installation_id: number | null;
  ingest_mode: RepositoryIngestMode;
  onboarding_status: string;
  created_at: string;
  archived_at: string | null;
}

/** A row in the installation's accessible-repos list. */
export interface InstallationRepo {
  owner: string;
  name: string;
  full_name: string;
  default_branch: string;
  private: boolean;
  /** Owner avatar URL from the provider; persisted on link. */
  avatar_url: string | null;
  is_linked: boolean;
  linked_repository_id: string | null;
}

export type BackfillStatusName = 'queued' | 'running' | 'succeeded' | 'failed' | 'superseded';
export interface BackfillProgress {
  stage: string;
  total_commits: number | null;
  commits_so_far: number;
  percent: number | null;
  at: string | null;
  head_sha: string | null;
  default_branch: string | null;
}
export interface Backfill {
  id: string;
  repository_id: string | null;
  status: BackfillStatusName;
  stats: unknown;
  progress?: BackfillProgress;
  error: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface OrgStatus {
  installations: Installation[];
  repos: Repo[];
  seed_status: 'needs_install' | 'needs_repo' | 'ready';
}

export interface CommitAuthor {
  login: string | null;
  name: string | null;
  email: string | null;
  date: string | null;
}

export interface CommitSummary {
  sha: string;
  short_sha: string;
  message_first_line: string;
  author: CommitAuthor;
  html_url: string;
}

export interface CommitFile {
  filename: string;
  status: string;
  additions: number;
  deletions: number;
  changes: number;
  previous_filename: string | null;
}

export interface CommitDetail {
  sha: string;
  message: string;
  author: CommitAuthor;
  html_url: string;
  stats: { additions: number; deletions: number; total: number };
  files: CommitFile[];
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Timeout for the compound view endpoints, which are a different animal from
 * the rest of the API.
 *
 * 15s was described as "generous for any flow we have today". It is not:
 * measured on prod over 7 days, view requests that SUCCEED run p50 1.78s /
 * **p95 47s**, so the default abort sits inside the successful distribution
 * rather than past its tail. What the user got at 15s was not a rescue from a
 * hang — it was a working request killed just before it answered, then a
 * `retry: 1` that started the whole compose again.
 *
 * 75s covers the measured p95 with room, and is still a bound rather than
 * "wait forever". The reason this is affordable at all is the server-side fix
 * that landed with it: an abandoned compose now still populates the cache
 * (`kv-cache.ts`), so even a request that DOES hit this ceiling leaves the next
 * one warm instead of leaving everyone cold forever.
 */
export const VIEW_TIMEOUT_MS = 75_000;

/** Endpoints that pay a cold compose and therefore get {@link VIEW_TIMEOUT_MS}. */
export function isViewRequest(path: string): boolean {
  return path.includes('/views/');
}

export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  // AbortController + timeout: never let a hung request leave the UI in
  // permanent isPending.
  const method = init?.method ?? 'GET';
  const timeoutMs = isViewRequest(path) ? VIEW_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  const startedAt = Date.now();

  // Don't log /v1/log itself (would recurse).
  const trackable = path !== '/v1/log';
  if (trackable) clog.debug('fetch_start', { method, path });

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      credentials: 'include',
      ...init,
      signal: controller.signal,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (trackable) {
        clog.error('fetch_timeout', { method, path, duration_ms: Date.now() - startedAt });
      }
      throw new HttpError(0, `${path} timed out after ${timeoutMs / 1000}s`);
    }
    if (trackable) {
      clog.error('fetch_network_error', {
        method,
        path,
        duration_ms: Date.now() - startedAt,
        message: err instanceof Error ? err.message : String(err),
      });
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (trackable) {
    clog.debug('fetch_end', {
      method,
      path,
      status: res.status,
      duration_ms: Date.now() - startedAt,
    });
  }

  // Feed the Worker's `X-Build-Hash` into the drift detector. First
  // observed hash latches; mismatches trip the banner that prompts the
  // user to refresh.
  observeBuildHash(res.headers.get('X-Build-Hash'));

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new HttpError(res.status, (body as { message?: string }).message ?? `${path} returned ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Query keys ----------------------------------------------------------------

export const queryKeys = {
  me: ['me'] as const,
  orgDetail: (slug: string) => ['orgs', slug, 'detail'] as const,
  orgStatus: (slug: string) => ['orgs', slug, 'status'] as const,
  orgInstallations: (slug: string) => ['orgs', slug, 'installations'] as const,
  orgRepos: (slug: string) => ['orgs', slug, 'repos'] as const,
  repoCommits: (slug: string, repoId: number) =>
    ['orgs', slug, 'repos', repoId, 'commits'] as const,
  commitDetail: (slug: string, repoId: number, sha: string) =>
    ['orgs', slug, 'repos', repoId, 'commits', sha] as const,
  // New-schema (string-id `repo_<hash>`) repository resources used by
  // the Repositories settings page and backfill flow.
  repositories: (slug: string) => ['orgs', slug, 'repositories'] as const,
  teams: (slug: string) => ['orgs', slug, 'teams'] as const,
  installationRepos: (slug: string, installId: number) =>
    ['orgs', slug, 'installations', installId, 'repos'] as const,
  backfillsForRepo: (slug: string, repoId: string) =>
    ['orgs', slug, 'backfills', { repository_id: repoId }] as const,
  onboardingsForRepo: (slug: string, repoId: string) =>
    ['orgs', slug, 'onboardings', { repository_id: repoId }] as const,
};

// Auth state ----------------------------------------------------------------

export type AuthQueryResult =
  | { status: 'loading' }
  | { status: 'signed-in'; me: MeResponse }
  | { status: 'signed-out' }
  | { status: 'error'; message: string };

/**
 * Wraps `useMe` into the discriminated `AuthQueryResult` shape that pages
 * branch on. 401 → signed-out (data path, not error), other errors →
 * `error`.
 */
export function useAuth(): AuthQueryResult {
  const q = useMe();
  const result: AuthQueryResult = (() => {
    if (q.isPending) return { status: 'loading' };
    if (q.isError) {
      if (q.error instanceof HttpError && q.error.status === 401) {
        return { status: 'signed-out' };
      }
      return { status: 'error', message: q.error.message };
    }
    return { status: 'signed-in', me: q.data };
  })();

  // Log only on state transitions, not on every render.
  const last = useRef<string | null>(null);
  useEffect(() => {
    const key =
      result.status === 'error'
        ? `error:${result.message}`
        : result.status === 'signed-in'
          ? `signed-in:${result.me.user.id}`
          : result.status;
    if (last.current !== key) {
      last.current = key;
      clog.info('auth_transition', {
        status: result.status,
        is_pending: q.isPending,
        is_error: q.isError,
        error_status: q.error instanceof HttpError ? q.error.status : null,
        // Avoid `error_name` / `error_message` — those are reserved
        // top-level fields in the server's logger envelope (see
        // libs/logger/src/transports/json-transport.ts) and would be
        // namespaced to `client_error_*` by clog.ts. Using prefixed
        // names keeps the observability schema flatter.
        auth_err_name: q.error?.name ?? null,
        auth_err_message: q.error?.message ?? null,
        fetch_status: q.fetchStatus,
      });
    }
  }, [result, q.isPending, q.isError, q.error, q.fetchStatus]);

  return result;
}

export function useMe(): UseQueryResult<MeResponse, Error> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => fetchJson<MeResponse>('/v1/users/me'),
    retry: (failureCount, error) => {
      // Don't retry on 401 — that's a "signed out" signal, not an error.
      if (error instanceof HttpError && error.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
  });
}

// Org status ----------------------------------------------------------------

export function useOrgStatus(orgSlug: string | null) {
  return useQuery({
    queryKey: orgSlug ? queryKeys.orgStatus(orgSlug) : ['orgs', null, 'status'],
    queryFn: () =>
      fetchJson<{ status: OrgStatus }>(
        `/v1/orgs/${encodeURIComponent(orgSlug!)}/status`,
      ).then((r) => r.status),
    enabled: !!orgSlug,
    staleTime: 10_000,
  });
}

/**
 * Polls /v1/orgs/<slug>/installations until `targetInstallationId` is
 * present in the result. Stops polling once found (or `enabled = false`).
 * Used by the post-install "Connected" landing.
 */
export function useInstallationsPolling(args: {
  orgSlug: string | null;
  targetInstallationId: number | null;
  enabled: boolean;
  pollMs?: number;
}): UseQueryResult<Installation[], Error> & { found: boolean } {
  const query = useQuery({
    queryKey: args.orgSlug
      ? [...queryKeys.orgInstallations(args.orgSlug), 'polling']
      : ['orgs', null, 'installations', 'polling'],
    queryFn: () =>
      fetchJson<{ installations: Installation[] }>(
        `/v1/orgs/${encodeURIComponent(args.orgSlug!)}/installations`,
      ).then((r) => r.installations),
    enabled: args.enabled && !!args.orgSlug,
    refetchInterval: (q) => {
      const data = q.state.data as Installation[] | undefined;
      if (!data || args.targetInstallationId == null) return args.pollMs ?? 1500;
      const found = data.some((i) => i.installation_id === args.targetInstallationId);
      return found ? false : (args.pollMs ?? 1500);
    },
  });

  const found =
    !!query.data &&
    args.targetInstallationId != null &&
    query.data.some((i) => i.installation_id === args.targetInstallationId);

  return Object.assign(query, { found });
}

// Repo commits --------------------------------------------------------------

export function useRecentCommits(orgSlug: string, repoId: number) {
  return useQuery({
    queryKey: queryKeys.repoCommits(orgSlug, repoId),
    queryFn: () =>
      fetchJson<{ commits: CommitSummary[] }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repos/${repoId}/commits`,
      ).then((r) => r.commits),
    staleTime: 30_000,
  });
}

export function useCommitDetail(orgSlug: string, repoId: number, sha: string | null) {
  return useQuery({
    queryKey: sha
      ? queryKeys.commitDetail(orgSlug, repoId, sha)
      : ['orgs', orgSlug, 'repos', repoId, 'commits', null],
    queryFn: () =>
      fetchJson<{ commit: CommitDetail }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repos/${repoId}/commits/${sha}`,
      ).then((r) => r.commit),
    enabled: !!sha,
    staleTime: 5 * 60_000, // commits are immutable; cache long
  });
}

// Mutations -----------------------------------------------------------------

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; slug?: string }) =>
      fetchJson<{ org: { id: number; name: string; slug: string; role: OrgRole } }>('/v1/orgs', {
        method: 'POST',
        body: JSON.stringify(args),
      }).then((r) => r.org),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * Rename an org (owner-only). Mutation arg carries the slug so a single
 * hook instance can rename any org from the cross-org Manage Orgs list.
 */
/** One applied merge: `survivor` absorbed `absorbed[]` (all within `repo`). */
export interface AppliedBundle {
  survivor: string;
  absorbed: string[];
  repo: string;
  /** Synthesized umbrella name for the survivor, when the naming pass ran. */
  display?: string;
}
export interface ReconcileResult {
  merges: AppliedBundle[];
  derived_contexts_deprecated: string[];
  clusters_updated: number;
  threshold: number;
}

/**
 * Reconcile an org: enqueue the co-change consolidation apply (bitemporal
 * derivedContext merges within each repo + cluster re-point + map refresh).
 * Owner-only, mutating. Returns a `job_id` — the caller polls
 * {@link useConsolidationJob} because the live-catalog scan far exceeds the
 * ~15s inline gateway timeout on a large org.
 */
export function useReconcile(orgSlug: string) {
  return useMutation({
    mutationFn: () =>
      fetchJson<{ job_id: string; status: 'queued' }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/consolidation`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
  });
}

export interface JobRow {
  id: string;
  kind: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  error_message: string | null;
  result_json: string | null;
}

/**
 * Poll a reconcile job to completion. Refetches while queued/running, stops on
 * a terminal state, and on success invalidates all caches so the refreshed
 * platform map + catalog refetch. Parses `result_json` into a {@link ReconcileResult}.
 */
export function useConsolidationJob(orgSlug: string, jobId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['orgs', orgSlug, 'jobs', jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const row = await fetchJson<JobRow>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/jobs/${encodeURIComponent(jobId!)}`,
      );
      if (row.status === 'succeeded') {
        void qc.invalidateQueries();
        return {
          ...row,
          result: row.result_json ? (JSON.parse(row.result_json) as ReconcileResult) : null,
        };
      }
      return { ...row, result: null };
    },
    refetchInterval: (q) => {
      const data = q.state.data as { status: string } | undefined;
      if (data && (data.status === 'succeeded' || data.status === 'failed')) return false;
      return 2000;
    },
  });
}

export function useRenameOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, name }: { slug: string; name: string }) =>
      fetchJson<{ org: { id: number; name: string; slug: string; role: OrgRole } }>(
        `/v1/orgs/${encodeURIComponent(slug)}`,
        { method: 'PATCH', body: JSON.stringify({ name }) },
      ).then((r) => r.org),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export interface OrgDetail {
  id: number;
  name: string;
  slug: string;
  role: OrgRole;
  created_at: string;
  /** Prose the org supplied about its business. NULL when nobody has written one. */
  business_context: string | null;
}

/**
 * One org's detail record. Separate from the `/me` org list because
 * `business_context` is prose measured in kilobytes and only ever edited one
 * org at a time — putting it on `/me` would fatten every page load.
 */
export function useOrgDetail(orgSlug: string | null) {
  return useQuery({
    queryKey: orgSlug ? queryKeys.orgDetail(orgSlug) : ['orgs', null, 'detail'],
    queryFn: () =>
      fetchJson<{ org: OrgDetail }>(`/v1/orgs/${encodeURIComponent(orgSlug!)}`).then((r) => r.org),
    enabled: !!orgSlug,
  });
}

/**
 * Set (or clear) an org's business context (owner-only) — the prose brief the
 * historian reads as a set of questions to investigate. Pass null or an empty
 * string to clear it. Only the org's own detail query is invalidated; `/me`
 * doesn't carry this field.
 */
export function useSetOrgBusinessContext() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, businessContext }: { slug: string; businessContext: string | null }) =>
      fetchJson<{ org: OrgDetail }>(`/v1/orgs/${encodeURIComponent(slug)}`, {
        method: 'PATCH',
        body: JSON.stringify({ business_context: businessContext }),
      }).then((r) => r.org),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: queryKeys.orgDetail(vars.slug) });
    },
  });
}

/**
 * Set (or clear) an org's icon (owner-only). The icon is a small image
 * data URL produced client-side by the crop/upload editor; pass null to
 * remove it. Orgs come from the `/me` query, so we invalidate that.
 */
export function useSetOrgIcon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, icon }: { slug: string; icon: string | null }) =>
      fetchJson<{ org: { id: number; name: string; slug: string; icon: string | null } }>(
        `/v1/orgs/${encodeURIComponent(slug)}`,
        { method: 'PATCH', body: JSON.stringify({ icon }) },
      ).then((r) => r.org),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * A team — an org-scoped grouping of engineers. Every engineer is on
 * exactly one team; `member_ids` lists the public engineer ids explicitly
 * assigned to a (non-default) team. The default team's members are
 * "everyone not on another team", derived client-side.
 */
export interface Team {
  id: number;
  name: string;
  is_default: boolean;
  member_ids: string[];
}

export function useTeams(orgSlug: string | null) {
  return useQuery({
    queryKey: orgSlug ? queryKeys.teams(orgSlug) : ['orgs', null, 'teams'],
    queryFn: () =>
      fetchJson<{ teams: Team[] }>(`/v1/orgs/${encodeURIComponent(orgSlug!)}/teams`).then(
        (r) => r.teams,
      ),
    enabled: !!orgSlug,
    staleTime: 30_000,
  });
}

export function useCreateTeam(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      fetchJson<{ team: Team }>(`/v1/orgs/${encodeURIComponent(orgSlug)}/teams`, {
        method: 'POST',
        body: JSON.stringify({ name }),
      }).then((r) => r.team),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.teams(orgSlug) });
    },
  });
}

export function useRenameTeam(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, name }: { teamId: number; name: string }) =>
      fetchJson<{ team: Team }>(`/v1/orgs/${encodeURIComponent(orgSlug)}/teams/${teamId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }).then((r) => r.team),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.teams(orgSlug) });
    },
  });
}

export function useDeleteTeam(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: number) =>
      fetchJson<void>(`/v1/orgs/${encodeURIComponent(orgSlug)}/teams/${teamId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.teams(orgSlug) });
    },
  });
}

/** Replace a team's membership (move semantics — one team per engineer). */
export function useSetTeamMembers(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, engineerIds }: { teamId: number; engineerIds: string[] }) =>
      fetchJson<{ ok: true }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/teams/${teamId}/members`,
        { method: 'PUT', body: JSON.stringify({ engineer_ids: engineerIds }) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.teams(orgSlug) });
    },
  });
}

/** Move an engineer to a team (or null to revert to the default team). */
export function useAssignEngineerTeam(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ engineerId, teamId }: { engineerId: string; teamId: number | null }) =>
      fetchJson<{ ok: true }>(`/v1/orgs/${encodeURIComponent(orgSlug)}/teams/assignment`, {
        method: 'PUT',
        body: JSON.stringify({ engineer_id: engineerId, team_id: teamId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.teams(orgSlug) });
    },
  });
}

/**
 * Hard-delete an org and everything under it (owner-only). The backend
 * refuses to delete the caller's only org (409 `last_org`).
 */
export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) =>
      fetchJson<void>(`/v1/orgs/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * Hard-delete a repository from an org (owner-only). Wipes its ingested
 * history; the repo stays available to re-add from the install list.
 */
export function useDeleteRepository() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgSlug, repoId }: { orgSlug: string; repoId: string }) =>
      fetchJson<{ ok: true }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repositories/${encodeURIComponent(repoId)}`,
        { method: 'DELETE' },
      ).then(() => ({ orgSlug })),
    onSuccess: ({ orgSlug }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.repositories(orgSlug) });
      void qc.invalidateQueries({ queryKey: ['orgs', orgSlug, 'installations'] });
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useStartInstall(orgSlug: string) {
  return useMutation({
    mutationFn: (args: { github_account_login?: string } = {}) =>
      fetchJson<{ install_url: string; pending_install_id: string; expires_at: string }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/installations`,
        { method: 'POST', body: JSON.stringify(args) },
      ),
  });
}

export function useAddRepo(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      installation_id: number;
      repo_full_name: string;
      default_branch?: string;
    }) =>
      fetchJson<{ repo: Repo }>(`/v1/orgs/${encodeURIComponent(orgSlug)}/repos`, {
        method: 'POST',
        body: JSON.stringify(args),
      }).then((r) => r.repo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.orgStatus(orgSlug) });
    },
  });
}

export interface ConnectSourceResult {
  repository: Repository;
  ingest_run_id: string | null;
  backfill_status: 'spawned' | 'noop' | 'unavailable';
}

/**
 * Connect a token-authenticated source (GitLab / Azure Repos). Paste a
 * repo URL + access token; the API stores the token encrypted, registers
 * the repo in the new-schema `repositories` table, and kicks a backfill.
 * GitHub uses the App install flow instead (`useStartInstall`).
 */
export function useConnectSource(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { repo_url: string; token: string; default_branch?: string }) =>
      fetchJson<ConnectSourceResult>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/source-connections`,
        { method: 'POST', body: JSON.stringify(args) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.repositories(orgSlug) });
    },
  });
}

// New-schema repositories + backfill --------------------------------

export function useRepositories(orgSlug: string | null) {
  return useQuery({
    queryKey: orgSlug ? queryKeys.repositories(orgSlug) : ['orgs', null, 'repositories'],
    queryFn: () =>
      fetchJson<{ repositories: Repository[] }>(
        `/v1/orgs/${encodeURIComponent(orgSlug!)}/repositories`,
      ).then((r) => r.repositories),
    enabled: !!orgSlug,
    staleTime: 10_000,
  });
}

export function useInstallationRepos(orgSlug: string | null, installationId: number | null) {
  return useQuery({
    queryKey:
      orgSlug && installationId
        ? queryKeys.installationRepos(orgSlug, installationId)
        : ['orgs', orgSlug, 'installations', installationId, 'repos'],
    queryFn: () =>
      fetchJson<{ repos: InstallationRepo[] }>(
        `/v1/orgs/${encodeURIComponent(orgSlug!)}/installations/${installationId}/repos`,
      ).then((r) => r.repos),
    enabled: !!orgSlug && !!installationId,
    staleTime: 30_000,
  });
}

/** Create a new-schema `repositories` row (used by the "Link" action). */
export function useCreateRepository(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      installation_id?: number;
      owner: string;
      name: string;
      default_branch?: string;
      /** Omit (don't send null) when absent — the route's zod schema
       *  validates a URL string and treats the field as optional. */
      avatar_url?: string;
      ingest_mode?: RepositoryIngestMode;
    }) =>
      fetchJson<{ repository: Repository }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repositories`,
        { method: 'POST', body: JSON.stringify(args) },
      ).then((r) => r.repository),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.repositories(orgSlug) });
      void qc.invalidateQueries({ queryKey: ['orgs', orgSlug, 'installations'] });
      // Linking auto-kicks an onboarding run server-side. Poke the
      // onboarding poll so the tracker banner appears immediately
      // (matrix-view empty states + the data-sources page all render it)
      // instead of waiting up to one 5s poll for the user to see that
      // anything happened. Key matches useActiveOnboarding.
      void qc.invalidateQueries({ queryKey: ['onboarding', 'latest', orgSlug] });
    },
  });
}

/**
 * Link a repo into an org chosen at call time. Same contract as
 * `useCreateRepository`, but the destination org slug is a mutation
 * argument rather than baked into the hook — used by the link-time
 * org chooser, where the destination can be an existing org or one
 * the user just created (so the slug isn't known when the hook runs).
 * GitHub installation tokens are minted statelessly from the install
 * id, so a repo can be linked into a different org than the one the
 * install was registered under.
 */
export function useLinkRepoToOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgSlug,
      ...args
    }: {
      orgSlug: string;
      installation_id?: number;
      owner: string;
      name: string;
      default_branch?: string;
      /** Omit when absent — see useCreateRepository's note. */
      avatar_url?: string;
      ingest_mode?: RepositoryIngestMode;
    }) =>
      fetchJson<{ repository: Repository }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repositories`,
        { method: 'POST', body: JSON.stringify(args) },
      ).then((r) => ({ repository: r.repository, orgSlug })),
    onSuccess: ({ orgSlug }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.repositories(orgSlug) });
      void qc.invalidateQueries({ queryKey: ['orgs', orgSlug, 'installations'] });
      void qc.invalidateQueries({ queryKey: ['onboarding', 'latest', orgSlug] });
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * Kick off a backfill. Returns `{ ingest_run_id, status }` where
 * `status` is `'spawned'` on a fresh container or `'noop'` when the
 * HEAD-sha skip-check matched the latest succeeded run. Pass
 * `force: true` to bypass the skip-check.
 */
export function useStartBackfill(orgSlug: string, repoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { force?: boolean } = {}) =>
      fetchJson<{ ingest_run_id: string; status: 'spawned' | 'noop' }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repos/${encodeURIComponent(repoId)}/backfills`,
        { method: 'POST', body: JSON.stringify(args) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.backfillsForRepo(orgSlug, repoId) });
    },
  });
}

/**
 * Force-reset: wipe every backfill-derived row for a repo and spawn
 * a fresh backfill in one round-trip. Distinct from `useStartBackfill`
 * — that UPSERTs onto existing rows; this one drops them first. Used
 * when walker behavior / prefix normalization / filter logic has
 * changed and stale rows need to clear. See
 * `apps/api/src/handlers/reset-backfill.ts` for the wipe set.
 */
export function useResetBackfill(orgSlug: string, repoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson<{
        deleted: Record<string, number>;
        new_ingest_run_id: string;
        status: 'spawned';
      }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repos/${encodeURIComponent(repoId)}/backfill_resets`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.backfillsForRepo(orgSlug, repoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.onboardingsForRepo(orgSlug, repoId) });
    },
  });
}

/**
 * Most recent backfill for a repo. While `running`, polls with
 * exponential backoff — first refetch at ~2s (catches early stage
 * transitions like cloning → walking), doubling each cycle, capped
 * at 30s. Stops on terminal status.
 *
 * The "early transitions are interesting, late polling is wasted"
 * shape matches the workload: walk goes from `starting` →
 * `cloning` → `walking` in the first few seconds, then sits at
 * `walking` for the rest of the run (minutes).
 */
const BACKFILL_POLL_MIN_MS = 2_000;
const BACKFILL_POLL_MAX_MS = 30_000;
function backfillPollIntervalMs(dataUpdateCount: number): number {
  return Math.min(
    BACKFILL_POLL_MAX_MS,
    BACKFILL_POLL_MIN_MS * Math.pow(2, Math.max(0, dataUpdateCount - 1)),
  );
}
export function useLatestBackfill(orgSlug: string | null, repoId: string | null) {
  return useQuery({
    queryKey:
      orgSlug && repoId
        ? queryKeys.backfillsForRepo(orgSlug, repoId)
        : ['orgs', orgSlug, 'backfills', { repository_id: repoId }],
    queryFn: () =>
      fetchJson<{ backfills: Backfill[] }>(
        `/v1/orgs/${encodeURIComponent(orgSlug!)}/backfills?repository_id=${encodeURIComponent(
          repoId!,
        )}&limit=1`,
      ).then((r) => r.backfills[0] ?? null),
    enabled: !!orgSlug && !!repoId,
    refetchInterval: (q) => {
      const data = q.state.data as Backfill | null | undefined;
      if (data && data.status !== 'running') return false;
      return backfillPollIntervalMs(q.state.dataUpdateCount);
    },
  });
}

/**
 * Same shape as the most-recent backfill summary but for the cloud
 * LLM onboarding flow. Used by the Repositories settings page to
 * decide whether to show "Onboard" or "Re-run onboarding."
 */
export interface OnboardingSummary {
  id: string;
  repository_id: string | null;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  current_step: 'signals' | 'bfs' | 'derivedContexts' | 'engineers' | 'observations' | null;
  started_at: string;
  completed_at: string | null;
  error_kind: string | null;
  error_message: string | null;
}

/**
 * Kick off (or re-run) cloud onboarding for a repo. Returns
 * `{ onboarding_run_id, status: 'spawned' }`. The server doesn't
 * enforce "only one at a time" for the same repo today; the SPA
 * disables the button while a run is in flight to avoid the obvious
 * double-fire.
 *
 * Pass `steps` to run only part of the pipeline — the "Re-skill" action
 * uses `['signals','derivedContexts']` to re-derive derivedContexts + skills
 * against the org's current business functions (pinned automatically
 * server-side) without the slow BF pass or engineer/observation rebuild.
 * Omit `steps` for a full re-onboard.
 */
export function useStartOnboarding(orgSlug: string, repoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { steps?: string[] } = {}) =>
      fetchJson<{ onboarding_run_id: string; status: 'spawned' }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/repos/${encodeURIComponent(repoId)}/onboardings`,
        { method: 'POST', body: JSON.stringify(args.steps ? { steps: args.steps } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.onboardingsForRepo(orgSlug, repoId) });
    },
  });
}

/**
 * Most recent onboarding run for a repo. Polls every 5s while
 * `queued`/`running` (the cloud-onboarding banner uses the same
 * cadence). Stops on terminal status.
 */
export function useLatestOnboarding(orgSlug: string | null, repoId: string | null) {
  return useQuery({
    queryKey:
      orgSlug && repoId
        ? queryKeys.onboardingsForRepo(orgSlug, repoId)
        : ['orgs', orgSlug, 'onboardings', { repository_id: repoId }],
    queryFn: () =>
      fetchJson<{ onboardings: OnboardingSummary[] }>(
        `/v1/orgs/${encodeURIComponent(orgSlug!)}/onboardings?repository_id=${encodeURIComponent(
          repoId!,
        )}&limit=1`,
      ).then((r) => r.onboardings[0] ?? null),
    enabled: !!orgSlug && !!repoId,
    refetchInterval: (q) => {
      const data = q.state.data as OnboardingSummary | null | undefined;
      if (data && (data.status === 'running' || data.status === 'queued')) return 5_000;
      return false;
    },
  });
}

/**
 * Saves a plan (project or team-change scaffold) to the org's plans
 * table. The roadmap view bundles plans into its payload — on success
 * we invalidate the roadmap-view query so the new plan appears in the
 * sidebar without a manual refetch.
 *
 * `orgSlug` null = signed-out / mock-data mode. The mutation surface
 * still exists; calling `mutate` rejects with a clear error. Callers
 * should disable the Save button in that mode.
 */
export function useCreatePlan(orgSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: string;
      display: string;
      kind: 'project' | 'team_change';
      payload: unknown;
    }) => {
      if (orgSlug == null) {
        return Promise.reject(new Error('Sign in to save plans.'));
      }
      return fetchJson<{ plan: { id: string } }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/plans`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.plan);
    },
    onSuccess: () => {
      if (orgSlug == null) return;
      // Roadmap view bundles plans; invalidating refreshes the sidebar.
      void qc.invalidateQueries({ queryKey: ['view', 'roadmap', orgSlug] });
    },
  });
}

/**
 * Update an existing plan's display + payload via PATCH. `id` is the
 * prefixed wire id (`plan_<slug>`) — the route handler unwraps it
 * server-side, matching the same pattern as the roadmap PATCH.
 */
export function useUpdatePlan(orgSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: { display?: string; payload?: unknown };
    }) => {
      if (orgSlug == null) {
        return Promise.reject(new Error('Sign in to update plans.'));
      }
      return fetchJson<{ plan: { id: string } }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/plans/${encodeURIComponent(args.id)}`,
        { method: 'PATCH', body: JSON.stringify(args.body) },
      ).then((r) => r.plan);
    },
    onSuccess: () => {
      if (orgSlug == null) return;
      void qc.invalidateQueries({ queryKey: ['view', 'roadmap', orgSlug] });
    },
  });
}

/**
 * Soft-delete a plan via DELETE. `id` is the prefixed wire id.
 * Roadmaps still referencing the deleted slug surface a dangling
 * reference (consistent with how the ledger handles deletes
 * elsewhere) — callers should clear the deleted id from any active
 * selection before invoking.
 */
export function useDeletePlan(orgSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (orgSlug == null) {
        return Promise.reject(new Error('Sign in to delete plans.'));
      }
      return fetchJson<{ deleted: true; id: string }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/plans/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: () => {
      if (orgSlug == null) return;
      void qc.invalidateQueries({ queryKey: ['view', 'roadmap', orgSlug] });
    },
  });
}

/**
 * Save the user's current free-form selection (projects + team-change
 * plan slugs, no roadmap active) as a new Roadmap. Backend payload
 * expects raw slugs — callers must strip the `plan_` / `tchg_`
 * prefixes before invoking.
 */
export function useCreateRoadmap(orgSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: string;
      display: string;
      team_change: string | null;
      projects: string[];
    }) => {
      if (orgSlug == null) {
        return Promise.reject(new Error('Sign in to save roadmaps.'));
      }
      return fetchJson<{ roadmap: { id: string } }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/roadmaps`,
        { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.roadmap);
    },
    onSuccess: () => {
      if (orgSlug == null) return;
      void qc.invalidateQueries({ queryKey: ['view', 'roadmap', orgSlug] });
    },
  });
}

/**
 * Persist the user's modifications to an existing roadmap (added or
 * removed projects, swapped team-change). `id` is the raw slug
 * (without the `rmap_` prefix) since it's used in the URL.
 */
export function useUpdateRoadmap(orgSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      body: { team_change?: string | null; projects?: string[] };
    }) => {
      if (orgSlug == null) {
        return Promise.reject(new Error('Sign in to update roadmaps.'));
      }
      return fetchJson<{ roadmap: { id: string } }>(
        `/v1/orgs/${encodeURIComponent(orgSlug)}/roadmaps/${encodeURIComponent(args.id)}`,
        { method: 'PATCH', body: JSON.stringify(args.body) },
      ).then((r) => r.roadmap);
    },
    onSuccess: () => {
      if (orgSlug == null) return;
      void qc.invalidateQueries({ queryKey: ['view', 'roadmap', orgSlug] });
    },
  });
}
